import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Sidebar } from "@/components/layout/sidebar";
import { Header } from "@/components/layout/header";
import { ImpersonationBanner } from "@/components/superadmin/impersonation-banner";
import { LifecycleBanner } from "@/components/layout/lifecycle-banner";
import { QuotaBanner } from "@/components/layout/quota-banner";

interface AppShellProps {
  children: React.ReactNode;
  basePath?: string;
}

export function AppShell({ children, basePath = "/app" }: AppShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-4 focus:left-4 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>

      {/* Desktop sidebar */}
      <aside className="hidden w-60 shrink-0 border-r bg-background lg:block">
        <div className="flex h-full flex-col">
          <Sidebar basePath={basePath} />
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      <MobileSidebarPortal
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        basePath={basePath}
      />

      {/* Main area */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <ImpersonationBanner basePath={basePath} />
        <LifecycleBanner basePath={basePath} />
        <QuotaBanner basePath={basePath} />
        <Header
          onToggleSidebar={() => setMobileOpen(!mobileOpen)}
          basePath={basePath}
        />
        <main id="main-content" className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}

/**
 * Mobile sidebar panel rendered as a fixed overlay.
 * Uses a lightweight approach instead of Sheet to keep things simple.
 */
function MobileSidebarPortal({
  open,
  onClose,
  basePath,
}: {
  open: boolean;
  onClose: () => void;
  basePath: string;
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <button
        type="button"
        aria-label="Close navigation menu"
        className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm lg:hidden"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed inset-y-0 left-0 z-50 w-60 border-r bg-background shadow-lg lg:hidden">
        <button
          type="button"
          className="absolute right-2 top-2 rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          onClick={onClose}
          aria-label="Close menu"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="pt-8">
          <Sidebar onNavigate={onClose} basePath={basePath} />
        </div>
      </div>
    </>
  );
}
